/* =====================================================================
   PokéTroveFinds — Theme JS
   Scroll reveals, scroll progress, mobile drawer, magnetic hovers,
   product gallery, quantity controls.
   ===================================================================== */

(function () {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal via IntersectionObserver ---------------- */
  function initReveal() {
    const items = document.querySelectorAll('[data-reveal], [data-stagger]');
    if (!items.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });
    items.forEach((el) => io.observe(el));
  }

  /* ---------- Scroll progress bar ----------------------------------- */
  function initScrollProgress() {
    const bar = document.querySelector('.scroll-progress__bar');
    if (!bar) return;
    let ticking = false;
    function update() {
      const h = document.documentElement;
      const scrolled = h.scrollTop;
      const total = h.scrollHeight - h.clientHeight;
      const pct = total > 0 ? (scrolled / total) * 100 : 0;
      bar.style.width = pct + '%';
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    update();
  }

  /* ---------- Header shrink on scroll ------------------------------ */
  function initHeaderShrink() {
    const header = document.querySelector('[data-site-header]');
    if (!header) return;
    function update() {
      header.classList.toggle('is-scrolled', window.scrollY > 16);
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ---------- Mobile drawer menu ------------------------------------ */
  function initMobileMenu() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const drawer = document.querySelector('[data-mobile-drawer]');
    if (!toggle || !drawer) return;
    const close = drawer.querySelector('[data-menu-close]');

    function open() {
      drawer.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('menu-open');
    }
    function shut() {
      drawer.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('menu-open');
    }

    toggle.addEventListener('click', () => {
      drawer.classList.contains('is-open') ? shut() : open();
    });
    if (close) close.addEventListener('click', shut);
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer) shut();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) shut();
    });
  }

  /* ---------- Product gallery (thumb selection) -------------------- */
  function initProductGallery() {
    const gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;
    const main = gallery.querySelector('[data-gallery-main]');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    if (!main || !thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.src;
        if (!src) return;
        // Cross-fade
        main.style.opacity = '0';
        setTimeout(() => {
          main.src = src;
          main.style.opacity = '1';
        }, 160);
        thumbs.forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  /* ---------- Quantity stepper -------------------------------------- */
  function initQtySteppers() {
    document.querySelectorAll('[data-qty]').forEach((wrap) => {
      const input = wrap.querySelector('input');
      const dec = wrap.querySelector('[data-qty-dec]');
      const inc = wrap.querySelector('[data-qty-inc]');
      if (!input) return;
      const min = parseInt(input.min || '1', 10);
      if (dec) dec.addEventListener('click', () => {
        const v = parseInt(input.value || '1', 10);
        input.value = Math.max(min, v - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      if (inc) inc.addEventListener('click', () => {
        const v = parseInt(input.value || '1', 10);
        input.value = v + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ---------- Card tilt on pointer (subtle) ------------------------ */
  function initCardTilt() {
    if (reducedMotion) return;
    const cards = document.querySelectorAll('[data-tilt]');
    cards.forEach((card) => {
      let rect = null;
      card.addEventListener('pointerenter', () => { rect = card.getBoundingClientRect(); });
      card.addEventListener('pointermove', (e) => {
        if (!rect) rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        card.style.transform = `perspective(900px) rotateX(${(-y * 4).toFixed(2)}deg) rotateY(${(x * 4).toFixed(2)}deg) translateY(-6px)`;
      });
      card.addEventListener('pointerleave', () => {
        card.style.transform = '';
        rect = null;
      });
    });
  }

  /* ---------- Add-to-cart button ripple/pulse ---------------------- */
  function initCartPulse() {
    document.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
      btn.addEventListener('click', () => {
        btn.classList.remove('is-pulsing');
        // Force reflow to restart animation
        void btn.offsetWidth;
        btn.classList.add('is-pulsing');
      });
    });
  }

  /* ---------- Loading state on subscribe links + form submits ------ */
  function initLoadingStates() {
    document.querySelectorAll('[data-loading-link]').forEach((link) => {
      link.addEventListener('click', () => {
        link.classList.add('is-loading');
      });
    });
    document.querySelectorAll('form').forEach((form) => {
      form.addEventListener('submit', () => {
        const submit = form.querySelector('button[type="submit"], input[type="submit"]');
        if (submit && !submit.disabled) submit.classList.add('is-loading');
      });
    });
  }

  /* ---------- Scroll-driven video frame sequence ------------------- */
  function initScrollVideo() {
    const sections = document.querySelectorAll('[data-scroll-video]');
    if (!sections.length) return;

    sections.forEach((sec) => {
      const canvas = sec.querySelector('[data-scroll-video-canvas]');
      const fallback = sec.querySelector('[data-scroll-video-fallback]');
      const frameCount = parseInt(sec.dataset.frameCount || '0', 10);
      const tpl = sec.dataset.frameUrlTemplate;
      const reverse = sec.dataset.reverse === 'true';
      const burnStart = parseFloat(sec.dataset.burnStart || '0'); // 0 = no burn phase
      const fit = sec.dataset.fit || 'cover';                     // 'cover' | 'contain'
      const coverHeader = sec.dataset.coverHeader === 'true';     // hide header until scrolled past
      if (!canvas || !frameCount || !tpl) return;

      // Build the URL pattern: replace the trailing "001" in the first frame's URL.
      const m = tpl.match(/^(.*?)(\d{3})(\.[a-z0-9]+)([?#].*)?$/i);
      if (!m) return;
      const prefix = m[1];
      const ext = m[3];
      const query = m[4] || '';
      const urlFor = (i) => `${prefix}${String(i).padStart(3, '0')}${ext}${query}`;

      const ctx = canvas.getContext('2d');
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      // Preload frames in priority order: 1, last, mid, then fill in.
      const frames = new Array(frameCount);
      let loadedCount = 0;
      let drawnFrameIdx = -1;

      function loadFrame(i) {
        if (frames[i]) return frames[i];
        const img = new Image();
        img.decoding = 'async';
        img.src = urlFor(i + 1); // user-facing 1-indexed
        frames[i] = img;
        img.onload = () => {
          loadedCount++;
          // If this frame is currently the target, redraw.
          if (i === drawnFrameIdx) draw(i);
          if (loadedCount === frameCount) sec.classList.add('js-all-loaded');
        };
        img.onerror = () => { frames[i] = null; };
        return img;
      }

      // Prioritized preload order
      const order = [];
      order.push(0);
      order.push(frameCount - 1);
      // bisection
      const queue = [[0, frameCount - 1]];
      while (queue.length && order.length < frameCount) {
        const [lo, hi] = queue.shift();
        const mid = Math.floor((lo + hi) / 2);
        if (mid !== lo && mid !== hi && !order.includes(mid)) {
          order.push(mid);
          queue.push([lo, mid]);
          queue.push([mid, hi]);
        }
      }
      // Kick off loads (browser will throttle)
      order.forEach((i) => loadFrame(i));

      // Sizing
      function resize() {
        const r = canvas.getBoundingClientRect();
        canvas.width = Math.round(r.width * dpr);
        canvas.height = Math.round(r.height * dpr);
        if (drawnFrameIdx >= 0) draw(drawnFrameIdx);
      }

      function draw(i) {
        const img = frames[i];
        if (!img || !img.complete || !img.naturalWidth) return;
        const cw = canvas.width, ch = canvas.height;
        const ir = img.naturalWidth / img.naturalHeight;
        const cr = cw / ch;
        let dw, dh, dx, dy;
        if (fit === 'contain') {
          // Whole frame visible (letterboxed)
          if (ir > cr) { dw = cw; dh = dw / ir; dx = 0; dy = (ch - dh) / 2; }
          else { dh = ch; dw = dh * ir; dx = (cw - dw) / 2; dy = 0; }
        } else {
          // cover — fills, crops overflow
          if (ir > cr) { dh = ch; dw = dh * ir; dx = (cw - dw) / 2; dy = 0; }
          else { dw = cw; dh = dw / ir; dx = 0; dy = (ch - dh) / 2; }
        }
        ctx.clearRect(0, 0, cw, ch);
        ctx.drawImage(img, dx, dy, dw, dh);
        if (fallback && !sec.classList.contains('js-canvas-ready')) {
          sec.classList.add('js-canvas-ready');
        }
      }

      function findNearestLoaded(target) {
        // Walk outward until we find a loaded frame
        for (let d = 0; d < frameCount; d++) {
          const a = target - d;
          const b = target + d;
          if (a >= 0 && frames[a] && frames[a].complete && frames[a].naturalWidth) return a;
          if (b < frameCount && frames[b] && frames[b].complete && frames[b].naturalWidth) return b;
        }
        return -1;
      }

      let pending = false;
      function onScroll() {
        if (pending) return;
        pending = true;
        requestAnimationFrame(() => {
          pending = false;
          const rect = sec.getBoundingClientRect();
          const vh = window.innerHeight || document.documentElement.clientHeight;
          // Progress 0..1 across the section's scroll travel.
          // Section is taller than viewport; sticky stage stays for (height - vh).
          const total = rect.height - vh;
          const passed = -rect.top;
          let p = total > 0 ? passed / total : 0;
          p = Math.max(0, Math.min(1, p));
          const scrolled = passed > 0;
          if (reverse) p = 1 - p;

          // Burn phase: frames finish scrubbing by `burnStart`, then the map
          // dissolves from center-out while `--burn` ramps 0 -> 1 over the tail.
          let frameP = p;
          if (burnStart > 0 && burnStart < 1) {
            frameP = Math.min(1, p / burnStart);
            const burn = Math.max(0, Math.min(1, (p - burnStart) / (1 - burnStart)));
            sec.style.setProperty('--burn', burn.toFixed(4));
            sec.classList.toggle('is-burning', burn > 0.001);
          }

          const target = Math.min(frameCount - 1, Math.max(0, Math.round(frameP * (frameCount - 1))));

          if (scrolled) sec.classList.add('is-scrubbing');
          else sec.classList.remove('is-scrubbing');

          // Hide the site header while the preview still fills the screen;
          // reveal it once the user scrolls past the preview into the page.
          if (coverHeader) {
            const rootEl = document.documentElement;
            const pastPreview = rect.bottom <= vh + 2;
            rootEl.classList.toggle('preview-done', pastPreview);
          }

          // Eagerly preload a small window around target so future scroll feels snappy
          for (let k = -3; k <= 3; k++) {
            const idx = target + k;
            if (idx >= 0 && idx < frameCount) loadFrame(idx);
          }

          const drawIdx = (frames[target] && frames[target].complete && frames[target].naturalWidth)
            ? target
            : findNearestLoaded(target);
          if (drawIdx >= 0 && drawIdx !== drawnFrameIdx) {
            drawnFrameIdx = drawIdx;
            draw(drawIdx);
          } else if (drawIdx === drawnFrameIdx) {
            // Already drawn this frame; nothing to do.
          }
        });
      }

      // Init
      resize();
      window.addEventListener('resize', resize);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      // Reduced motion: hold on the visible-at-start frame (no scrubbing).
      if (reducedMotion) {
        const idx = reverse ? frameCount - 1 : 0;
        loadFrame(idx);
        const img = frames[idx];
        if (img.complete && img.naturalWidth) draw(idx);
        else img.addEventListener('load', () => draw(idx));
        window.removeEventListener('scroll', onScroll);
      }
    });
  }

  /* ---------- Mystery Box subscription popup ----------------------- */
  function initMysteryBoxPopup() {
    const popup = document.querySelector('[data-mb-popup]');
    if (!popup) return;

    const storageKey = popup.dataset.storageKey || 'ptf_mbsub_dismissed_v1';
    const delay = parseInt(popup.dataset.delay || '8', 10) * 1000;
    const closeBtns = popup.querySelectorAll('[data-mb-popup-close]');
    const triggerBtns = document.querySelectorAll('[data-mb-popup-open]');
    const form = popup.querySelector('[data-mb-popup-form]');
    const successEl = popup.querySelector('.mb-popup__success');

    function dismissed() {
      try { return localStorage.getItem(storageKey) === '1'; } catch (e) { return false; }
    }
    function markDismissed() {
      try { localStorage.setItem(storageKey, '1'); } catch (e) {}
    }
    function open() {
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('menu-open');
    }
    function close(remember) {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('menu-open');
      if (remember !== false) markDismissed();
    }

    closeBtns.forEach((b) => b.addEventListener('click', () => close(true)));
    popup.addEventListener('click', (e) => {
      if (e.target === popup) close(true);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.classList.contains('is-open')) close(true);
    });

    triggerBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        open();
      });
    });

    if (form && successEl) {
      form.addEventListener('submit', (e) => {
        const email = form.querySelector('input[type="email"]');
        const tier  = form.querySelector('select');
        if (!email.value || !tier.value) {
          e.preventDefault();
          email.focus();
          return;
        }
        // Optimistic UI: hide the form and show success — Shopify handles submission via form action.
        // We don't block submission so the contact request reaches Shopify.
        setTimeout(() => {
          form.style.display = 'none';
          successEl.hidden = false;
          markDismissed();
        }, 50);
      });
    }

    // Auto-open after delay (if not dismissed and delay > 0)
    if (delay > 0 && !dismissed()) {
      setTimeout(() => {
        if (!dismissed()) open();
      }, delay);
    }

    // Expose for inline triggers
    window.PTFMysteryBox = { open, close };
  }

  /* ---------- Tear divider: cinematic, scroll-reactive torn edge ------ */
  /* Each divider carries inline SVG displacement filters (see
     snippets/tear-divider.liquid). This loop makes the torn edge feel
     CINEMATIC and ALIVE:
       • Multi-wave organic breathing (3 sine waves at irrational ratios:
         1.0 / 0.618 / 1.347) — the motion never repeats, always organic.
       • Scroll VELOCITY is smoothed via a 3-frame rolling average into a
         0..1 reaction that RUPTURES the edge harder the faster you scroll,
         then eases back with a very slow, cinematic release.
       • Noise-field baseFrequency wanders ±15% for contour morphing, and
         the turbulence seed periodically cycles to genuinely reshape the tear.
       • An independent fiber displacement layer adds fine hair-like detail
         at the tear line, driven at its own phase.
       • --p (scroll progress) and --v (reaction) are written to each visible
         divider for the CSS parallax / glow / shear response.
     Only dividers in (or near) the viewport are touched — gated by an
     IntersectionObserver — and the rAF loop parks itself once everything is
     off-screen and the reaction has settled, so it costs nothing at rest. */
  function initTearDivider() {
    const bands = Array.from(document.querySelectorAll('[data-tear-divider]'));
    if (!bands.length || reducedMotion) return; // CSS keeps a static torn edge

    // Pre-generate a pool of seeds for periodic cycling
    const seedPool = Array.from({ length: 12 }, (_, i) => 3 + ((i * 37) % 89));

    const items = bands.map((band, idx) => {
      const turb  = band.querySelector('[data-tear-turb]');
      const turb2 = band.querySelector('[data-tear-turb2]');
      const disp  = band.querySelector('[data-tear-disp]');
      const fibTurb = band.querySelector('[data-tear-fib-turb]');
      const fibDisp = band.querySelector('[data-tear-fib-disp]');
      const bf = (turb && turb.getAttribute('baseFrequency') || '0.009 0.014').split(/\s+/).map(Number);
      const bf2 = (turb2 && turb2.getAttribute('baseFrequency') || '0.025 0.04').split(/\s+/).map(Number);
      const bfFib = (fibTurb && fibTurb.getAttribute('baseFrequency') || '0.04 0.08').split(/\s+/).map(Number);
      const intensity = parseFloat(getComputedStyle(band).getPropertyValue('--tear-intensity')) || 1;
      return {
        band, disp, turb, turb2, fibTurb, fibDisp,
        fx: bf[0], fy: bf[1] || bf[0],
        fx2: bf2[0], fy2: bf2[1] || bf2[0],
        ffx: bfFib[0], ffy: bfFib[1] || bfFib[0],
        intensity,
        visible: false,
        seedIdx: idx % seedPool.length,
        lastSeedTime: 0,
      };
    });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const it = items.find((i) => i.band === e.target);
          if (it) it.visible = e.isIntersecting;
        });
        start();
      }, { rootMargin: '30% 0px 30% 0px' });
      items.forEach((i) => io.observe(i.band));
    } else {
      items.forEach((i) => { i.visible = true; });
    }

    let lastY = window.scrollY || window.pageYOffset || 0;
    let react = 0;                 // smoothed scroll reaction, 0..1
    let phase = 0;                 // breathing phase (organic, never-repeating)
    let lastFreq = 0;              // throttle baseFrequency rewrites
    let last = performance.now();
    let running = false;

    // 3-frame rolling average for velocity smoothing
    const velHistory = [0, 0, 0];
    let velIdx = 0;

    function frame(now) {
      const dt = Math.min(64, now - last);
      last = now;
      phase += dt * 0.0016;

      const y = window.scrollY || window.pageYOffset || 0;
      const dy = y - lastY;
      lastY = y;

      // Rolling-average velocity: smoother, more cinematic than single-frame
      velHistory[velIdx] = Math.abs(dy);
      velIdx = (velIdx + 1) % velHistory.length;
      const avgVel = (velHistory[0] + velHistory[1] + velHistory[2]) / 3;

      // Velocity → reaction: snap up FAST, release VERY slowly for cinematic tension
      const target = Math.min(1, avgVel / 50);
      react += (target - react) * (target > react ? 0.65 : 0.03);
      if (react < 0.0005) react = 0;

      const half = (window.innerHeight || document.documentElement.clientHeight) / 2;
      const driftFreq = now - lastFreq > 80; // ~12fps organic field drift
      if (driftFreq) lastFreq = now;
      let active = false;

      for (const it of items) {
        if (!it.visible) continue;
        active = true;
        const rect = it.band.getBoundingClientRect();
        let p = (half - (rect.top + rect.height / 2)) / (half * 2);
        p = p < -1 ? -1 : p > 1 ? 1 : p;

        it.band.style.setProperty('--p', p.toFixed(3));
        it.band.style.setProperty('--v', react.toFixed(3));

        // ─── Main edge displacement ───
        // Multi-wave organic breathing: 3 sine waves at irrational ratios
        // so the motion NEVER repeats — feels organic, cinematic, alive
        if (it.disp) {
          const breath1 = Math.sin(phase * 1.0 + p * 1.8) * 3.5;
          const breath2 = Math.sin(phase * 0.618 + p * 0.9) * 2.0;
          const breath3 = Math.sin(phase * 1.347 + p * 2.5) * 1.5;
          const breathTotal = breath1 + breath2 + breath3; // range ≈ ±7

          // Rest: 14 ± 7, scroll rupture adds up to +40
          const scale = 14 + breathTotal + react * 40 * it.intensity;
          it.disp.setAttribute('scale', scale.toFixed(2));
        }

        // ─── Fiber displacement ───
        // Independent phase, smaller range for fine detail
        if (it.fibDisp) {
          const fibBreath = Math.sin(phase * 1.7 + p * 3.1) * 1.2
                          + Math.sin(phase * 0.83) * 0.8;
          const fibScale = 3 + fibBreath + react * 6 * it.intensity;
          it.fibDisp.setAttribute('scale', fibScale.toFixed(2));
        }

        // ─── Noise-field wander + seed cycling ───
        if (driftFreq) {
          // baseFrequency wander: ±15% (was ±5%) — contour morphs visibly
          if (it.turb) {
            const wob1 = 1 + Math.sin(phase * 0.7) * 0.12 + Math.sin(phase * 0.31) * 0.05;
            it.turb.setAttribute('baseFrequency',
              (it.fx * wob1).toFixed(5) + ' ' + (it.fy * wob1).toFixed(5));
          }
          // Second turbulence wanders at a different rate
          if (it.turb2) {
            const wob2 = 1 + Math.sin(phase * 0.53 + 1.2) * 0.10 + Math.sin(phase * 0.89) * 0.06;
            it.turb2.setAttribute('baseFrequency',
              (it.fx2 * wob2).toFixed(5) + ' ' + (it.fy2 * wob2).toFixed(5));
          }
          // Fiber turbulence wanders independently
          if (it.fibTurb) {
            const wob3 = 1 + Math.sin(phase * 1.1 + 0.7) * 0.08;
            it.fibTurb.setAttribute('baseFrequency',
              (it.ffx * wob3).toFixed(5) + ' ' + (it.ffy * wob3).toFixed(5));
          }

          // Periodic seed cycling: every ~4.5 seconds, pick a new seed
          // so the tear contour genuinely reshapes, not just wobbles
          if (now - it.lastSeedTime > 4500) {
            it.lastSeedTime = now;
            it.seedIdx = (it.seedIdx + 1) % seedPool.length;
            const newSeed = seedPool[it.seedIdx];
            if (it.turb) it.turb.setAttribute('seed', newSeed);
            // Stagger the second turbulence seed
            if (it.turb2) it.turb2.setAttribute('seed', newSeed + 17);
          }
        }
      }

      // Keep animating while any divider is on screen, or while the reaction
      // is still easing out; otherwise park until the next scroll.
      if (active || react > 0) {
        requestAnimationFrame(frame);
      } else {
        running = false;
      }
    }

    function start() {
      if (running) return;
      running = true;
      last = performance.now();
      requestAnimationFrame(frame);
    }

    window.addEventListener('scroll', start, { passive: true });
    window.addEventListener('resize', start, { passive: true });
    start();
  }

  /* ---------- Magnetic buttons (playful pointer attraction) -------- */
  function initMagnetic() {
    if (reducedMotion) return;
    document.querySelectorAll('[data-magnetic]').forEach((el) => {
      const strength = parseFloat(el.dataset.magnetic) || 0.3;
      el.style.willChange = 'transform';
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        const mx = e.clientX - r.left - r.width / 2;
        const my = e.clientY - r.top - r.height / 2;
        el.style.transform = `translate(${(mx * strength).toFixed(1)}px, ${(my * strength * 1.4).toFixed(1)}px)`;
      });
      el.addEventListener('pointerleave', () => { el.style.transform = ''; });
    });
  }

  /* ---------- Boot --------------------------------------------------- */
  function boot() {
    initReveal();
    initScrollProgress();
    initHeaderShrink();
    initMobileMenu();
    initProductGallery();
    initQtySteppers();
    initCardTilt();
    initCartPulse();
    initLoadingStates();
    initScrollVideo();
    initTearDivider();
    initMysteryBoxPopup();
    initMagnetic();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
